import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BarChart3 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import type { InstanceLlmUsageSummary } from "@/types/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCompactNumber } from "@/lib/utils";

/** Default look-back window for the block: last 30 days. */
function last30Days(): { dateFrom: string; dateTo: string } {
  const to = new Date();
  const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
  return { dateFrom: from.toISOString(), dateTo: to.toISOString() };
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

/**
 * "LLM token usage" card on the instance page. Aggregated over the last 30
 * days — headline tiles + a top-models breakdown, with a link through to the
 * full filterable usage table. Tokens only; no cost is tracked. The data is
 * proxied live from the instance's own DB, so it can be `unreachable`.
 */
export function InstanceLlmUsageSection({ name }: { name: string }) {
  const { t } = useTranslation("instances");
  const { dateFrom, dateTo } = last30Days();

  const { data, isLoading } = useQuery({
    queryKey: ["instance", name, "llm-usage"],
    queryFn: () =>
      api.get<InstanceLlmUsageSummary>(
        `/api/admin/instances/${name}/llm-usage?dateFrom=${encodeURIComponent(
          dateFrom
        )}&dateTo=${encodeURIComponent(dateTo)}`
      ),
  });

  const totals = data?.totals ?? null;
  const topModels = (data?.byModel ?? []).slice(0, 5);
  const hasUsage = !!totals && totals.calls > 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>{t("llmUsage.title")}</CardTitle>
            <CardDescription>
              {t("llmUsage.description")}
            </CardDescription>
          </div>
          <Button size="sm" variant="secondary" asChild>
            <Link to={`/instances/${name}/usage`}>
              <BarChart3 className="mr-2 h-4 w-4" /> {t("llmUsage.viewDetails")}
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : data?.unreachable ? (
          <p className="py-2 text-sm text-muted-foreground">
            {t("llmUsage.unreachable")}
          </p>
        ) : !hasUsage ? (
          <p className="py-2 text-sm text-muted-foreground">
            {t("llmUsage.empty")}
          </p>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Tile
                label={t("llmUsage.totalTokens")}
                value={formatCompactNumber(totals.totalTokens)}
              />
              <Tile
                label={t("llmUsage.inputTokens")}
                value={formatCompactNumber(totals.inputTokens)}
              />
              <Tile
                label={t("llmUsage.outputTokens")}
                value={formatCompactNumber(totals.outputTokens)}
              />
              <Tile
                label={t("llmUsage.apiCalls")}
                value={formatCompactNumber(totals.calls)}
              />
            </div>

            <div>
              <h3 className="text-sm font-medium">{t("llmUsage.topModels")}</h3>
              <div className="mt-2 divide-y">
                {topModels.map((m) => (
                  <div
                    key={`${m.provider}:${m.model}`}
                    className="flex flex-wrap items-center gap-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <span className="block truncate font-medium">
                        {m.model}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {m.provider}
                      </span>
                    </div>
                    <Badge variant="secondary">
                      {t("llmUsage.calls", {
                        value: formatCompactNumber(m.calls),
                      })}
                    </Badge>
                    <span className="text-sm tabular-nums text-muted-foreground">
                      {t("llmUsage.tokens", {
                        value: formatCompactNumber(m.totalTokens),
                      })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
